import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  FaHome,
  FaTh,
  FaUser,
  FaShoppingBag,
  FaShoppingCart,
  FaQuestionCircle
} from 'react-icons/fa';
import tw from 'tailwind-styled-components';
import { toast } from 'react-toastify';
import EmailLoginModal from './EmailLoginModal';
import LoginModal from '../Pages/User/Login';
import SignupModal from '../Pages/User/Register';
import { Set_Cart_Modal_Open } from '../Redux/actions';

const BottomNavigation = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer.user);
  const isCartModalOpen = useSelector((state) => state.CartReducer.isCartModalOpen);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isOTPLoginModalOpen, setIsOTPLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  // Login/Register Modal Handlers 
  const handleOpenLoginModal = () => {
    setIsLoginModalOpen(true);
    setIsSignupModalOpen(false);
    setIsOTPLoginModalOpen(false);
  };

  const handleCloseAuthModals = () => {
    setIsLoginModalOpen(false);
    setIsOTPLoginModalOpen(false);
    setIsSignupModalOpen(false);
  };

  const handleSwitchToSignup = () => {
    handleCloseAuthModals();
    setIsSignupModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    handleCloseAuthModals();
    setIsLoginModalOpen(true);
  };

  const handleSwitchToOTPLogin = () => {
    handleCloseAuthModals();
    setIsOTPLoginModalOpen(true);
  };

  const handleLoginSuccess = () => {
    handleCloseAuthModals();
    toast.success('Login successful!');
  };

  const handleSignupSuccess = () => {
    handleCloseAuthModals();
    toast.success('Signup successful!');
  };

  const handleAccountClick = (event) => {
    if (!user) {
      event.preventDefault(); // Stop the Link from navigating to profile and open the login modal
      handleOpenLoginModal();
    }
    // If user exists, do nothing, allowing the Link to navigate normally
  };

  const handleCartClick = (event) => {
    event.preventDefault(); // Stop navigation to '/cart'
    if (user) {
      dispatch(Set_Cart_Modal_Open(true)); // Open Cart Modal
    } else {
      handleOpenLoginModal(); // Open Login Modal if not logged in
    }
  };

  const navItems = [
    {
      id: 1,
      label: 'Home',
      icon: FaHome,
      path: '/',
      isActive: location.pathname === '/'
    },
    {
      id: 2,
      label: 'Categories',
      icon: FaTh,
      path: '/categories',
      isActive: location.pathname === '/categories'
    },
    {
      id: 3,
      label: 'Account',
      icon: FaUser,
      path: '/profile',
      isActive: location.pathname === '/profile'
    },
    {
      id: 4,
      label: 'My Orders',
      icon: FaShoppingBag,
      path: '/orders',
      isActive: location.pathname === '/orders'
    },
    // {
    //   id: 5,
    //   label: 'Help',
    //   icon: FaQuestionCircle,
    //   path: '/help',
    //   isActive: location.pathname === '/help'
    // }
    {
      id: 5,
      label: 'Cart',
      icon: FaShoppingCart,
      path: '/cart',
      isActive: false
    },
  ];

  // Don't render navigation when cart is open
  if (isCartModalOpen) {
    return null;
  }

  return (
    <>
      <BottomNavContainer>
        <NavWrapper>
          {navItems.map((item) => {
            const IconComponent = item.icon;    // icons
            const isAccountTab = item.path === '/profile';
            const isCartTab = item.label === 'Cart';
            if (isCartTab) {
              return (
                <NavButton
                  key={item.id}
                  onClick={handleCartClick}
                >
                  <NavIcon $isActive={false}>
                    <IconComponent />
                  </NavIcon>
                  <NavLabel $isActive={false}>
                    {item.label}
                  </NavLabel>
                </NavButton>
              );
            }
            return (
              <NavLink                  // navigation link for each item
                key={item.id}
                to={item.path}
                $isActive={item.isActive}
                onClick={isAccountTab ? handleAccountClick : undefined}
              >
                <NavIcon $isActive={item.isActive}>
                  <IconComponent />
                </NavIcon>
                <NavLabel $isActive={item.isActive}>  {/* Navigation label for each item */}
                  {item.label}
                </NavLabel>
              </NavLink>
            );
          })}
        </NavWrapper>
      </BottomNavContainer>
      <Spacer /> {/* Spacer to prevent content from being hidden behind the nav */}
      <EmailLoginModal
        isOpen={isLoginModalOpen}
        onClose={handleCloseAuthModals}
        onSwitchToSignup={handleSwitchToSignup}
        onSwitchToEmailLogin={handleSwitchToOTPLogin}
        onLoginSuccess={handleLoginSuccess}
      />

      <LoginModal
        isOpen={isOTPLoginModalOpen}
        onClose={handleCloseAuthModals}
        onSwitchToSignup={handleSwitchToSignup}
        onSwitchToLogin={handleSwitchToLogin}
        onLoginSuccess={handleLoginSuccess}
      />

      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={handleCloseAuthModals}
        onSwitchToLogin={handleSwitchToLogin}
        onSignupSuccess={handleSignupSuccess}
      />
    </>
  );
};


// Styled Components
const BottomNavContainer = tw.nav`
  fixed bottom-0 left-0 right-0 
  bg-kuality-blue border-t border-gray-200 shadow-lg 
  z-50 md:hidden
`;

const NavWrapper = tw.div`
  flex justify-around items-center h-16 px-2 
  max-w-screen-xl mx-auto
`;

const NavLink = tw(Link)`
  flex flex-col items-center justify-center 
  flex-1 py-3 px-0.5 
  transition-all duration-200 relative
  ${(props) => props.$isActive
    ? 'border-t-4 border-white'
    : 'border-t-4 border-transparent'
  }
`;

const NavIcon = tw.div`
  text-lg mb-1 transition-colors duration-200
  ${(props) => props.$isActive ? 'text-white' : 'text-white'}
`;

const NavLabel = tw.span`
  text-xs font-medium transition-colors duration-200 leading-tight
  ${(props) => props.$isActive ? 'text-white' : 'text-white'}
`;

const Spacer = tw.div`
  w-full h-20 md:hidden block
`;

const NavButton = tw.button`
  flex flex-col items-center justify-center 
  flex-1 py-3 px-0.5 
  transition-all duration-200 relative
  border-t-4 border-transparent
  bg-transparent outline-none focus:outline-none
`;

export default BottomNavigation;